/**
 * Affiliate-link configuration.
 *
 * Single source of truth for every affiliate / partner / referral link
 * across the site. Each entry maps a logical key → product metadata +
 * URL. Pages reference entries by key, so when DRR signs up for a
 * partner program and gets a tracked URL, it's a one-line update here
 * and every consuming page picks up the new link.
 *
 * Style guide:
 * - Plain product URLs ship today (links work, no tracking).
 * - When tracking IDs arrive, replace `url` with the tagged variant.
 *   Amazon: append `?tag=YOUR_AMAZON_ID` (or `&tag=` if URL already has params).
 *   Impact / ShareASale / CJ: replace whole URL with the network-issued deeplink.
 * - The `merchant` and `network` fields drive GA4 event attribution.
 *
 * FTC compliance:
 * - All consumer-facing affiliate links must be rendered via the
 *   <AffiliateLink> component, which adds rel="sponsored nofollow" and
 *   fires an `affiliate_click` GA4 event.
 * - Pages that include any of these MUST also display the disclosure
 *   block from <AffiliateDisclosureNotice>.
 *
 * Arizona compliance:
 * - No public-adjuster, claim-mill, or insurance-product affiliates.
 *   A.R.S. § 20-450 (anti-rebate) and § 20-469 (public adjuster rules)
 *   create regulatory exposure for AZ contractors who collect kickbacks
 *   in insurance contexts. Restrict affiliates to consumer/contractor
 *   tools, training, software, and adjacent SaaS.
 */

export type AffiliateNetwork =
  | "Amazon Associates"
  | "Impact"
  | "ShareASale"
  | "CJ Affiliate"
  | "Direct"
  | "Pending";

export type AffiliateEntry = {
  /** Display name shown in UI cards. */
  name: string;
  /** One-line description shown under the name. */
  description: string;
  /** URL — plain product link until tracking ID is added. */
  url: string;
  /** Merchant name — used for GA4 event attribution. */
  merchant: string;
  /** Network — affects approval workflow and how to update the URL. */
  network: AffiliateNetwork;
  /** Approximate commission, for internal reference (NOT shown to users). */
  commission?: string;
  /** Optional category — used for grouping in UI. */
  category?: string;
};

export const AFFILIATES = {
  /* ----- Amazon Associates — Detection & Inspection ----- */
  delmhorst_bd2100: {
    name: "Delmhorst BD-2100 Moisture Meter",
    description: "Pin-type and pinless. The dual-mode standard for residential and light-commercial moisture mapping. We carry these in every truck.",
    url: "https://www.amazon.com/s?k=Delmhorst+BD-2100+moisture+meter&tag=drr20-20",
    merchant: "Amazon",
    network: "Amazon Associates",
    commission: "1-3%",
    category: "detection",
  },
  general_tools_mmd4e: {
    name: "General Tools MMD4E Pin-Type Moisture Meter",
    description: "Budget-friendly pin meter. Useful for property managers running periodic spot-checks before issues escalate.",
    url: "https://www.amazon.com/s?k=General+Tools+MMD4E+pin-type+moisture+meter&tag=drr20-20",
    merchant: "Amazon",
    network: "Amazon Associates",
    commission: "1-3%",
    category: "detection",
  },
  flir_c3x: {
    name: "FLIR C3-X Pocket Thermal Imager",
    description: "Detects hidden moisture and HVAC anomalies through walls without invasive cuts. The same physics our techs use on every loss.",
    url: "https://www.amazon.com/s?k=FLIR+C3-X+thermal+imager&tag=drr20-20",
    merchant: "Amazon",
    network: "Amazon Associates",
    commission: "1-3%",
    category: "detection",
  },
  govee_leak_detector: {
    name: "Govee Wi-Fi Water Leak Detectors (3-pack)",
    description: "Place under sinks, behind toilets, near water heaters, and in supply closets. App alerts catch leaks before they soak drywall.",
    url: "https://www.amazon.com/s?k=Govee+water+leak+detector+wifi&tag=drr20-20",
    merchant: "Amazon",
    network: "Amazon Associates",
    commission: "1-3%",
    category: "detection",
  },
  yolink_leak_sensor: {
    name: "YoLink LoRa Smart Leak Sensors",
    description: "Long-range version for bigger buildings. Sensors talk to a hub via LoRa instead of Wi-Fi — works in areas Wi-Fi can't reach.",
    url: "https://www.amazon.com/s?k=YoLink+water+leak+sensor+lora&tag=drr20-20",
    merchant: "Amazon",
    network: "Amazon Associates",
    commission: "1-3%",
    category: "detection",
  },
  teslong_borescope: {
    name: "Teslong NTS500 Borescope",
    description: "Inspect inside wall cavities, drains, ductwork. We use these to confirm hidden moisture before opening a wall.",
    url: "https://www.amazon.com/s?k=Teslong+borescope+inspection+camera&tag=drr20-20",
    merchant: "Amazon",
    network: "Amazon Associates",
    commission: "1-3%",
    category: "detection",
  },

  /* ----- Amazon Associates — Drying (residential / small-commercial) ----- */
  bluedri_bd130p: {
    name: "BlueDri BD-130P LGR Dehumidifier",
    description: "130-pint LGR for serious residential or small-commercial drying. Lower-cost residential alternative to the Phoenix LGR units we run in the field.",
    url: "https://www.amazon.com/s?k=BlueDri+BD-130P+LGR+dehumidifier&tag=drr20-20",
    merchant: "Amazon",
    network: "Amazon Associates",
    commission: "1-3%",
    category: "drying",
  },
  bair_vp25: {
    name: "B-Air VP-25 Air Mover",
    description: "Direct-drive centrifugal air mover used by restoration crews nationwide. Stackable, daisy-chainable.",
    url: "https://www.amazon.com/s?k=B-Air+VP-25+air+mover+blower&tag=drr20-20",
    merchant: "Amazon",
    network: "Amazon Associates",
    commission: "1-3%",
    category: "drying",
  },
  xpower_x2480a: {
    name: "XPOWER X-2480A HEPA Air Scrubber",
    description: "Negative-air-capable HEPA scrubber. The same class of equipment we set up for mold and drywall demo containments.",
    url: "https://www.amazon.com/s?k=XPOWER+X-2480A+HEPA+air+scrubber&tag=drr20-20",
    merchant: "Amazon",
    network: "Amazon Associates",
    commission: "1-3%",
    category: "drying",
  },

  /* ----- Amazon Associates — PPE ----- */
  tyvek_400: {
    name: "DuPont Tyvek 400 Coveralls",
    description: "Standard-issue PPE for biohazard, mold, and contaminated demolition work. Wear it any time you'd rather not bring building debris home with you.",
    url: "https://www.amazon.com/s?k=DuPont+Tyvek+400+coveralls&tag=drr20-20",
    merchant: "Amazon",
    network: "Amazon Associates",
    commission: "1-3%",
    category: "ppe",
  },
  three_m_6200: {
    name: '3M 6200 Half-Mask Respirator + P100 Cartridges',
    description: "Half-face respirator. P100 cartridges filter mold spores and most dust. Fit-test before relying on it.",
    url: "https://www.amazon.com/s?k=3M+6200+half+mask+respirator+P100&tag=drr20-20",
    merchant: "Amazon",
    network: "Amazon Associates",
    commission: "1-3%",
    category: "ppe",
  },
  moldex_n95: {
    name: "Moldex 2200 N95 Respirator (10-pack)",
    description: "Disposable N95s for short-duration mold inspections. For finer particulate, step up to a half-mask respirator.",
    url: "https://www.amazon.com/s?k=Moldex+2200+N95+respirator&tag=drr20-20",
    merchant: "Amazon",
    network: "Amazon Associates",
    commission: "1-3%",
    category: "ppe",
  },

  /* ----- B2B SaaS — Restoration & Documentation ----- */
  matterport_pro: {
    name: "Matterport Pro Plan",
    description: "3D space capture used on every large-loss DRR project. Insurance carriers and TPAs increasingly expect this level of documentation.",
    url: "https://matterport.com/",
    merchant: "Matterport",
    network: "Pending", // Switch to "Impact" once approved
    commission: "10% first-year",
    category: "documentation",
  },
  companycam_pro: {
    name: "CompanyCam",
    description: "Time-stamped, GPS-tagged job-site photo and video documentation. Live photo timeline shared with adjusters in real time.",
    url: "https://companycam.com",
    merchant: "CompanyCam",
    network: "Pending",
    commission: "$50-150/signup",
    category: "documentation",
  },
  encircle: {
    name: "Encircle",
    description: "End-to-end claims documentation platform. Floor plans, content inventories, photo claims — adjuster-ready output.",
    url: "https://www.getencircle.com/",
    merchant: "Encircle",
    network: "Pending",
    commission: "B2B referral",
    category: "documentation",
  },

  /* ----- B2B SaaS — Property & Facility Management ----- */
  buildium: {
    name: "Buildium",
    description: "Property management software for residential portfolios. Maintenance request workflows, owner portals, accounting in one stack.",
    url: "https://www.buildium.com",
    merchant: "Buildium",
    network: "Pending", // Impact when approved
    commission: "~$100/signup",
    category: "property-mgmt",
  },
  upkeep_cmms: {
    name: "UpKeep CMMS",
    description: "Mobile-first computerized maintenance management. Work-order tracking, asset history, preventive scheduling — built for facility teams.",
    url: "https://www.upkeep.com",
    merchant: "UpKeep",
    network: "Pending",
    commission: "~20% recurring",
    category: "facility-mgmt",
  },
  procore: {
    name: "Procore",
    description: "Construction project management — drawings, RFIs, submittals, scheduling. Used on most large reconstruction projects.",
    url: "https://www.procore.com",
    merchant: "Procore",
    network: "Pending",
    commission: "B2B referral",
    category: "facility-mgmt",
  },

  /* ----- Training & Continuing Education ----- */
  osha_com_hazwoper40: {
    name: "OSHA HAZWOPER 40-Hour Online (OSHA.com)",
    description: "Online HAZWOPER 40 — the same certification DRR field crews carry. Required for hazardous-waste site work under 29 CFR 1910.120.",
    url: "https://www.osha.com/courses/hazwoper-40-hour.html",
    merchant: "OSHA.com",
    network: "Pending", // ShareASale when approved
    commission: "~20%",
    category: "training",
  },
  three_sixty_training_iicrc: {
    name: "360training Restoration Course Catalog",
    description: "Continuing education for property managers and adjusters. Mold, water damage, OSHA, ICRA — recognized by most CE-credit programs.",
    url: "https://www.360training.com",
    merchant: "360training",
    network: "Pending",
    commission: "15-25%",
    category: "training",
  },
  linkedin_learning_facility: {
    name: "LinkedIn Learning — Facility Management Foundations",
    description: "60+ courses on facility management, construction PM, and operations. Essential pre-reading for new property managers.",
    url: "https://www.linkedin.com/learning/",
    merchant: "LinkedIn Learning",
    network: "Pending",
    commission: "$10/trial",
    category: "training",
  },

  /* ----- Reading List (Amazon Associates) ----- */
  book_paid_ed_cross: {
    name: "Paid! A Guide to Getting Paid as a Restoration Contractor — Ed Cross",
    description: "The contracts and billing playbook used across the restoration industry. Worth its weight in unpaid invoices avoided.",
    url: "https://www.amazon.com/s?k=Paid+Restoration+Contractor+Ed+Cross&tag=drr20-20",
    merchant: "Amazon",
    network: "Amazon Associates",
    commission: "4-4.5%",
    category: "reading",
  },
  book_insurance_claim_bible: {
    name: "The Insurance Claim Bible — Russel Whitener",
    description: "Practical claims-handling reference. Useful for property owners and PMs who want to understand carrier behavior before they need to.",
    url: "https://www.amazon.com/s?k=Insurance+Claim+Bible+Russel+Whitener&tag=drr20-20",
    merchant: "Amazon",
    network: "Amazon Associates",
    commission: "4-4.5%",
    category: "reading",
  },

  /* ----- Home Depot Pro (CJ Affiliate) ----- */
  home_depot_pro: {
    name: "Home Depot Pro Bulk Supplies",
    description: "Contractor-tier discounts on Tyvek, fans, dehumidifiers, plastic sheeting, ZipWall, and the rest of the daily restoration supply list.",
    url: "https://www.homedepot.com/c/pro_xtra_PRO",
    merchant: "Home Depot",
    network: "Pending", // CJ Affiliate when approved
    commission: "3%",
    category: "supplies",
  },
} as const satisfies Record<string, AffiliateEntry>;

export type AffiliateKey = keyof typeof AFFILIATES;

export function getAffiliate(key: AffiliateKey): AffiliateEntry {
  return AFFILIATES[key];
}
